import { FormDataEntryObj } from '@utils/types';
import { EntityMetadata, VisibleMetadataByName } from './types';
import {
    MetadataPropertyEntity,
    MetadataPropertyOptionEntity,
    MetadataPropertyType,
} from '@api/entities/replancity_MetadataProperty';

export const correctFormDataObjBeforeRequest = (
    formDataEntryObj: FormDataEntryObj,
    visibleMetadataByName: VisibleMetadataByName
) => {
    return Object.entries(formDataEntryObj).reduce((acc, [fieldName, value]) => {
        const { type } = visibleMetadataByName[fieldName];
        if (type === 'Integer') {
            const value = formDataEntryObj[fieldName];
            acc[fieldName] = value === '' ? null : parseInt(value as string);
        } else if (type === 'Double') {
            const value = formDataEntryObj[fieldName];
            acc[fieldName] = value === '' ? null : parseFloat(value as string);
        } else {
            acc[fieldName] = formDataEntryObj[fieldName];
        }
        return acc;
    }, {});
};

const fn = () => false;

const createShowIfFunction = function (fieldName: string, likeShowIfProp?: string) {
    if (likeShowIfProp) {
        return new Function(`
            try {
                return ${likeShowIfProp};
            } catch (e) {
                console.error(\`Corrupted ${likeShowIfProp}: "${likeShowIfProp}" for "${fieldName}" metadata\\n\`, e);
            return () => false;
        }`);
    }
    return fn;
};

const metadataOptionsToSelectOptions = (
    type: MetadataPropertyType,
    options: MetadataPropertyOptionEntity[]
) =>
    options.map(({ id, propertyName, printedName }) => ({
        value: type === 'Enum' ? propertyName : id,
        caption: printedName ?? propertyName,
    }));

export const getVisibleEntityMetadata = (
    metaData: MetadataPropertyEntity[],
    entityDraft: Record<string, unknown> = {},
    entity: Record<string, any> = {}
): {
    metadata: EntityMetadata;
    entityDraft: Record<string, unknown>;
} => {
    //TODO move to separate class/method
    const orderedNames: string[] = [];
    const visibleMetadataByName: VisibleMetadataByName = metaData
        .filter(({ showIf }) => !!showIf && showIf !== 'false')
        .reduce((acc, meta) => {
            orderedNames.push(meta.name);

            acc[meta.name] = {
                ...meta,
                options: metadataOptionsToSelectOptions(meta.type, meta.options),
                showIf: createShowIfFunction(meta.name, meta.showIf),
                mandatoryIf: createShowIfFunction(meta.name, meta.mandatoryIf),
            };

            const { name, type, showIf, mandatoryIf, defaultValue, options } = meta;

            const deps = showIf.match(/(?<=\bthis\.)\w+/g);
            if (deps) {
                deps.forEach(dep => {
                    if (!acc[dep]) {
                        // @ts-ignore
                        acc[dep] = {
                            subscribers: [],
                        };
                    }
                    if (!acc[dep].subscribers) {
                        acc[dep].subscribers = [];
                    }

                    acc[dep].subscribers.push(name);
                });
            }

            acc[name].showIf = createShowIfFunction(name, showIf);
            acc[name].mandatoryIf = createShowIfFunction(name, mandatoryIf);
            acc[name].options = metadataOptionsToSelectOptions(type, options);

            let curValue = '';
            if (options.length) {
                curValue = type === 'Enum' ? options[0].propertyName : options[0].id;
            }
            if (defaultValue) {
                curValue = defaultValue;
            }
            acc[name].defaultValue = curValue;

            // metadata value should be used if showIf and
            // entityDraft isn't saved in sessionStorage
            // or there isn't such key in stored data
            if (acc[name].showIf.apply(entityDraft) && !entityDraft[name]) {
                entityDraft[name] = curValue;
            }

            if (acc[name].showIf.apply(entity) && entity[name]) {
                entityDraft[name] = entity[name].toString();
            }

            return acc;
        }, {});

    return { metadata: { visibleMetadataByName, orderedNames }, entityDraft };
};

export const getVisibleFieldsByFormChange = ({
    fieldName,
    visibleMetadataByName,
    formDataObj,
}: {
    visibleMetadataByName: VisibleMetadataByName;
    fieldName: string;
    formDataObj: Record<string, unknown>;
}) => {
    const getNewFormDataObj = ({
        fieldName,
        visibleMetadataByName,
        formDataObj,
    }: {
        visibleMetadataByName: VisibleMetadataByName;
        fieldName: string;
        formDataObj: Record<string, unknown>;
    }) => {
        if (!visibleMetadataByName?.[fieldName]?.subscribers) {
            return formDataObj;
        }

        visibleMetadataByName?.[fieldName].subscribers.forEach(subscriber => {
            if (visibleMetadataByName[subscriber].showIf.apply(formDataObj)) {
                formDataObj[subscriber] = Object.hasOwn(formDataObj, subscriber)
                    ? formDataObj[subscriber]
                    : (visibleMetadataByName[subscriber].defaultValue ?? '');
            } else {
                delete formDataObj[subscriber];
            }
            return getNewFormDataObj({ fieldName: subscriber, visibleMetadataByName, formDataObj });
        });

        return formDataObj;
    };

    return getNewFormDataObj({ fieldName, visibleMetadataByName, formDataObj: { ...formDataObj } });
};
